import React from 'react';
import { Dialog } from '@mui/material';
import type { DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import { colors } from '../../../styles/theme/colors';
import cornerRipple from '../../../../assets/corner-ripple.svg';

interface DialogWrapperProps extends Omit<MuiDialogProps, 'BackdropProps'> {
  children: React.ReactNode;
  backdropOpacity?: 'light' | 'default' | 'dark';
  customBackdropColor?: string;
  disableBlur?: boolean;
}

/**
 * Reusable Dialog wrapper with consistent backdrop blur styling
 * As per CLAUDE.md: All dialog boxes must have backdrop-filter: blur(16px)
 *
 * @param backdropOpacity - Controls the darkness of the backdrop
 *   - 'light': rgba(0, 0, 0, 0.3) - lighter backdrop
 *   - 'default': rgba(0, 0, 0, 0.5) - standard backdrop
 *   - 'dark': rgba(0, 0, 0, 0.7) - darker backdrop
 * @param customBackdropColor - Override backdrop color completely
 * @param disableBlur - Disable the blur effect (not recommended per guidelines)
 */
export const DialogWrapper: React.FC<DialogWrapperProps> = ({
  children,
  backdropOpacity = 'default',
  customBackdropColor,
  disableBlur = false,
  PaperProps: callerPaperProps,
  ...dialogProps
}) => {
  const getBackdropColor = () => {
    if (customBackdropColor) return customBackdropColor;

    switch (backdropOpacity) {
      case 'light':
        return colors.overlay.backdropLight;
      case 'dark':
        return colors.overlay.backdropDark;
      case 'default':
      default:
        return colors.overlay.backdrop;
    }
  };

  const { sx: callerSx, ...restPaperProps } = (callerPaperProps ?? {}) as { sx?: object; [key: string]: unknown };

  return (
    <Dialog
      {...dialogProps}
      BackdropProps={{
        sx: {
          backdropFilter: disableBlur ? 'none' : 'blur(8px)',
          backgroundColor: getBackdropColor(),
        },
      }}
      PaperProps={{
        ...restPaperProps,
        sx: {
          position: 'relative',
          overflow: 'auto',
          ...callerSx,
        },
      }}
    >
      <img
        src={cornerRipple}
        alt=""
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '216px',
          height: '216px',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      {children}
    </Dialog>
  );
};
